// src/components/auth/register/PasswordReset.tsx
import React from "react";
import { Affix, Button, Card, Form, Input, Modal, Typography, Breadcrumb } from "antd";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { withFirebase, WithFirebaseProps } from "../../dev/Firebase/Firebase";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { MailOutlined, HomeOutlined } from "@ant-design/icons";
import "./PasswordReset.css";

const { Title, Text } = Typography;

// Define the form values type
interface PasswordResetFormValues {
  email: string;
}

const PasswordResetContainer = styled.div`
  animation: fadeIn 1.5s ease-in;
  background: linear-gradient(135deg, #FFC107 0%, #FFCA28 100%);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  width: 100%;
  box-sizing: border-box;
  position: relative;
`;

const PasswordResetCard = styled.div`
  width: 95%;
  max-width: 100%;
  margin: 0 auto;
  background: #fce248;
  box-shadow: 0 6px 20px rgba(255, 204, 0, 0.4);
  border: 1px solid #ffeb3b;
  border-radius: 12px;
  padding: 40px;
  box-sizing: border-box;
  position: relative;

  .ant-card {
    background: transparent;
  }

  @media (max-width: 768px) {
    width: 96% !important;
    padding: 25px;
  }

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const StyledForm = styled(Form)`
  padding: 24px;

  .ant-form-item-label > label {
    font-size: 18px;
    color: #0427f4;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ant-form-item {
    margin-bottom: 20px;
  }

  .ant-input {
    border-radius: 8px;
    padding: 8px;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
  }

  .ant-input:hover {
    border-color: #40c4ff;
  }

  .ant-input:focus {
    border-color: #40c4ff;
    box-shadow: 0 0 0 2px rgba(64, 196, 255, 0.2);
  }

  .ant-btn {
    font-size: 18px;
    border-radius: 8px;
    padding: 12px 24px;
    width: 100%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .ant-btn:hover,
  .ant-btn:focus {
    transform: scale(1.02);
  }

  .ant-btn:focus {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    padding: 15px;

    .ant-form-item-label > label,
    .ant-input,
    .ant-btn {
      font-size: 14px !important;
    }
  }

  @media (max-width: 480px) {
    .ant-form-item-label > label {
      font-size: 16px !important;
    }

    .ant-input,
    .ant-btn {
      font-size: 12px !important;
    }
  }
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #0427f4;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #40c4ff;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const BackButtonWrapper = styled(Affix)`
  position: absolute;
  left: 20px;
  top: 60px;

  .ant-btn {
    background-color: transparent;
    border: 1px solid #0427f4;
    color: #0427f4;
    transition: all 0.3s ease;
  }

  .ant-btn:hover,
  .ant-btn:focus {
    background-color: #0427f4;
    color: #fff;
    transform: scale(1.1);
  }

  .ant-btn:focus {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 50px;
  }
`;

const PasswordReset: React.FC<WithFirebaseProps> = ({ firebase }) => {
  const [form] = Form.useForm<PasswordResetFormValues>();
  const navigate = useNavigate();

  const handleSubmit = (values: PasswordResetFormValues) => {
    firebase
      .passwordReset(values.email)
      .then(() => {
        Modal.success({
          title: "Email Sent",
          content: "Check your email for instructions to reset your password.",
          onOk: () => navigate("/"),
        });
      })
      .catch((error: any) => {
        Modal.error({
          title: "Reset Failed",
          content: error.message,
        });
      });
  };

  return (
    <PasswordResetContainer className="password-reset-container">
      <Helmet>
        <title>Password Reset | onelightsystem OLS - Sun Light Meditation</title>
        <meta
          name="description"
          content="Reset your password for onelightsystem OLS to continue your Sun Light Meditation journey and access Light Civilization services. Follow the instructions sent to your email."
        />
        <meta
          name="keywords"
          content="OLS password reset, Sun Light Meditation, OLS login, Light Civilization, onelightsystem OLS, meditation courses, spiritual growth"
        />
        <meta name="author" content="onelightsystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Password Reset | onelightsystem OLS - Sun Light Meditation" />
        <meta
          property="og:description"
          content="Reset your password for onelightsystem OLS to continue your Sun Light Meditation journey and access Light Civilization services. Follow the instructions sent to your email."
        />
        <meta property="og:url" content="https://www.olsme.com/passwordreset" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="onelightsystem OLS Logo for Password Reset Page" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="onelightsystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:title" content="Password Reset | onelightsystem OLS - Sun Light Meditation" />
        <meta
          name="twitter:description"
          content="Reset your password for onelightsystem OLS to continue your Sun Light Meditation journey and access Light Civilization services. Follow the instructions sent to your email."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="onelightsystem OLS Logo for Password Reset Page" />
        <link rel="canonical" href="https://www.olsme.com/passwordreset" />
        <link rel="preload" href="/logo.jpg" as="image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Password Reset",
            "url": "https://www.olsme.com/passwordreset",
            "description": "Reset your password for onelightsystem OLS to continue your Sun Light Meditation journey and access Light Civilization services. Follow the instructions sent to your email.",
            "mainEntity": {
              "@type": "Service",
              "name": "Password Reset",
              "description": "Password reset service for onelightsystem OLS users to regain access to Sun Light Meditation education and Light Civilization community.",
              "provider": {
                "@type": "Organization",
                "name": "onelightsystem OLS",
                "url": "https://www.olsme.com",
                "logo": "https://www.olsme.com/logo.jpg",
                "contactPoint": {
                  "@type": "ContactPoint",
                  "contactType": "customer service",
                  "email": "contact@olsme.com",
                  "telephone": "+1-123-456-7890", // Replace with actual number if available
                },
                "sameAs": [
                  "https://www.linkedin.com/company/onelightsystem",
                  "https://twitter.com/onelightsystem",
                  "https://www.facebook.com/onelightsystem",
                  "https://www.instagram.com/onelightsystem",
                  "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
                ],
              },
            },
            "publisher": {
              "@type": "Organization",
              "name": "onelightsystem OLS",
              "url": "https://www.olsme.com",
              "logo": "https://www.olsme.com/logo.jpg",
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://www.olsme.com",
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Password Reset",
                  "item": "https://www.olsme.com/passwordreset",
                },
              ],
            },
          })}
        </script>
      </Helmet>
      <BreadcrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Item>
            <RouterLink to="/">
              <HomeOutlined /> Home
            </RouterLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Password Reset</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbWrapper>
      <BackButtonWrapper offsetTop={20}>
        <Button
          shape="circle"
          icon={<HomeOutlined />}
          onClick={() => navigate("/")}
          aria-label="Back to home"
        />
      </BackButtonWrapper>
      <PasswordResetCard className="password-reset-card">
        <Card>
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <Title level={1} style={{ color: "#0427f4", fontSize: "24px" }}>
              Reset Your Password
            </Title>
            <Text style={{ color: "#0427f4", display: "block", marginBottom: "20px" }}>
              Enter your email to receive password reset instructions
            </Text>
          </div>
          <StyledForm form={form} onFinish={handleSubmit} layout="vertical">
            <Form.Item
              label={
                <span>
                  <MailOutlined /> E-mail
                </span>
              }
              name="email"
              rules={[
                { type: "email", message: "The input is not a valid email!" },
                { required: true, message: "Please input your email!" },
              ]}
            >
              <Input aria-label="Email input" />
            </Form.Item>
            <Form.Item style={{ textAlign: "center" }}>
              <Button
                type="primary"
                htmlType="submit"
                aria-label="Reset password"
              >
                Reset Password
              </Button>
            </Form.Item>
            <Text style={{ color: "#0427f4", display: "block", textAlign: "center" }}>
              Need help?{" "}
              <RouterLink to="/contact" style={{ color: "#3552fc", textDecoration: "underline" }}>
                Contact us
              </RouterLink>{" "}
              or return to{" "}
              <RouterLink to="/login" style={{ color: "#3552fc", textDecoration: "underline" }}>
                Login
              </RouterLink>
            </Text>
          </StyledForm>
        </Card>
      </PasswordResetCard>
    </PasswordResetContainer>
  );
};

export default withFirebase(PasswordReset);